import React, { useState, useEffect, useRef, useMemo } from "react";
import { Download, Info, AlertCircle, TrendingUp, Send, X, ChevronRight } from "lucide-react";
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import axios from "axios";
const AnnualBudgetTracker = ({ budgetData, staticCostData }) => {
  const currentYear = new Date().getFullYear();
  const currentYearString = String(currentYear);
  const accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;
  const idToken = useSelector((state) => state.idToken.value);
  const decoded = jwtDecode(idToken);
  const userName = `${decoded.given_name} ${decoded.family_name}`;
  const userEmail = decoded.email || '';
  const annualBudget = useMemo(() => {
    const currentYearBudget = Array.isArray(budgetData)
      ? budgetData.find(item => String(item.Year) === currentYearString)?.Budget
      : budgetData?.Budget;

    return parseFloat(currentYearBudget) || 0;
  }, [budgetData, currentYearString]);

  const amountSpent = staticCostData?.yearToDateCost || 0;
  const percentageSpent = annualBudget > 0 ? (amountSpent / annualBudget) * 100 : 0;

  const [progressAnimation, setProgressAnimation] = useState(0);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [tooltipStyles, setTooltipStyles] = useState({});

  // Refs for click outside detection
  const analysisRef = useRef(null);
  const analyzeButtonRef = useRef(null);
  const monthMarkersRef = useRef([]);

  // Process monthly cost data from API
  const monthlySpendData = useMemo(() => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    // Create default array with zero spend
    const monthlyData = months.map(month => ({
      month,
      spend: 0
    }));

    if (staticCostData?.monthlyTotalCosts && staticCostData.monthlyTotalCosts.length > 0) {
      staticCostData.monthlyTotalCosts.forEach(item => {
        if (item.year === currentYear.toString()) {
          const monthIndex = parseInt(item.month) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            monthlyData[monthIndex].spend = item.cost || 0;
          }
        }
      });
    }

    return monthlyData;
  }, [staticCostData, currentYear]);


  useEffect(() => {
    setIsLoading(true);
    setProgressAnimation(0);
    const loadingTimer = setTimeout(() => {
      if (staticCostData && annualBudget > 0) {
        setIsLoading(false);
        setTimeout(() => animateProgressBar(), 100);
      }
    }, 1500);

    return () => clearTimeout(loadingTimer);
  }, [staticCostData, annualBudget]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAnalysis &&
        analysisRef.current &&
        !analysisRef.current.contains(event.target) &&
        analyzeButtonRef.current &&
        !analyzeButtonRef.current.contains(event.target)
      ) {
        setShowAnalysis(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAnalysis]);

  const animateProgressBar = () => {
    if (annualBudget <= 0 || typeof amountSpent !== 'number') {
      console.log("Skipping animation - no valid budget data");
      return;
    }

    setProgressAnimation(0.1);
    setTimeout(() => {
      const end = percentageSpent > 100 ? 100 : percentageSpent;
      setProgressAnimation(end);
    }, 100);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Budget tracker functions
  const hasMonthlyData = (monthIndex) => {
    return monthlySpendData[monthIndex].spend > 0;
  };

  const getMonthPosition = (monthIndex) => {
    if (monthIndex === 0) return 2;
    if (monthIndex === 11) return 98;
    return ((monthIndex / 11) * 100);
  };

  const getMonthPercentOfBudget = (spend) => {
    return annualBudget > 0 ? ((spend / annualBudget) * 100).toFixed(1) : "0.0";
  };


  const getCurrentMonth = () => {
    const now = new Date();
    return now.toLocaleString('default', { month: 'long', year: 'numeric' });
  };


  const getCurrentMonthIndex = () => {
    return new Date().getMonth();
  };


  const handleAnalyzeClick = () => {
    setShowAnalysis(!showAnalysis);
  };

  const handleGetDetailedReport = async () => {
    const query = `
    query MyQuery {
      generateAnnualReport {
        message
        success
      }
    }
  `;
    try {
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      console.log('Report generation response:', response.data);
      setShowNotification(true);
      setShowAnalysis(false);
    } catch (error) {
      console.error('Error generating report');
      setShowNotification(true);
      setShowAnalysis(false);
    }
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };


  const currentMonthForecast = staticCostData?.forecastedCost || 0;
  const currentMonthIndex = getCurrentMonthIndex();


  const averageMonthlySpend = useMemo(() => {
    console.log(monthlySpendData)
    const completedMonthsData = monthlySpendData.slice(0, currentMonthIndex);
    const totalSpendCompletedMonths = completedMonthsData.reduce((sum, data) => sum + data.spend, 0);
    const numberOfCompletedMonths = completedMonthsData.length;
    if (numberOfCompletedMonths === 0 && currentMonthIndex === 0) {
      return monthlySpendData[0]?.spend || 0;
    }

    return numberOfCompletedMonths > 0 ? totalSpendCompletedMonths / numberOfCompletedMonths : 0;
  }, [monthlySpendData, currentMonthIndex]);

  const projectedAnnualSpend = useMemo(() => {
    const monthsRemaining = 12 - (currentMonthIndex + 1);
    return amountSpent + (averageMonthlySpend * monthsRemaining);
  }, [amountSpent, averageMonthlySpend, currentMonthIndex]);

  const budgetStatus = useMemo(() => {
    if (amountSpent > annualBudget) {
      return {
        status: "At Risk",
        color: "text-red-600",
        bgColor: "bg-red-100",
        message: `You're projected to exceed your annual budget by ${formatCurrency(amountSpent - annualBudget)}`
      };
    } else if (amountSpent > annualBudget * 0.9) {
      return {
        status: "Warning",
        color: "text-yellow-600",
        bgColor: "bg-yellow-100",
        message: "You're close to your annual budget limit"
      };
    } else {
      return {
        status: "On Track",
        color: "text-[#003366]",
        bgColor: "bg-[#e6f0f9]",
        message: `You're on track to stay under budget by ${formatCurrency(annualBudget - amountSpent)}`
      };
    }
  }, [amountSpent, annualBudget]);

  // Find highest spending month
  const highestSpendingMonth = useMemo(() => {
    let maxSpend = 0;
    let maxIndex = 0;

    monthlySpendData.forEach((data, index) => {
      if (data.spend > maxSpend) {
        maxSpend = data.spend;
        maxIndex = index;
      }
    });

    return {
      month: monthlySpendData[maxIndex].month,
      spend: maxSpend
    };
  }, [monthlySpendData]);

  // Handle tooltip positioning
  const handleTooltipPosition = (index) => {
    if (monthMarkersRef.current[index]) {
      const markerRect = monthMarkersRef.current[index].getBoundingClientRect();
      const progressBarRect = monthMarkersRef.current[index].closest('.relative.w-full.h-5').getBoundingClientRect();

      setTooltipStyles({
        left: markerRect.left + (markerRect.width / 2),
        top: progressBarRect.bottom + 10,
        transform: 'translateX(-50%)',
      });

      setActiveTooltip(index);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 mb-2">
      {/* Notification ribbon */}
      {showNotification && (
        <div
          className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-50 border-l-4 border-green-700 text-green-700 p-4 rounded shadow-lg z-50 flex items-center"
          style={{ animation: 'fadeInOut 5s ease forwards' }}
        >
          <Send size={18} className="mr-2" />
          <span>Detailed report will be sent to your email address if you have required permissions.</span>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
        <div>
          <h3 className="text-lg font-bold text-[#003366]">Annual Budget Tracker ({currentYear})</h3>
          <p className="text-xs text-gray-600">Current spend as of {getCurrentMonth()}</p>
        </div>
        <div className="mt-2 md:mt-0 flex items-center text-sm">
          <span className="font-semibold text-[#003366]">{formatCurrency(amountSpent)}</span>
          <span className="mx-2 text-gray-500">of</span>
          <span className="font-semibold text-gray-600">{formatCurrency(annualBudget)}</span>
          <span className="ml-3 bg-[#003366] text-white text-xs py-1 px-2 rounded-full">
            {progressAnimation.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Progress Bar with Monthly Markers */}
      <div className="relative w-full h-5 bg-gray-200 rounded-full overflow-hidden">
        {/* Loading animation */}
        {isLoading && (
          <div className="absolute top-0 left-0 h-full w-full">
            <div className="h-full bg-blue-200 animate-pulse"></div>
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 w-1/3 animate-[progressLoading_1.5s_ease-in-out_infinite]"
              style={{
                animation: 'progressLoading 1.5s ease-in-out infinite',
                backgroundSize: '200% 100%'
              }}
            ></div>
          </div>
        )}

        {/* Actual progress bar (shown after loading) */}
        {!isLoading && (
          <>
            {/* Progress bar with improved animation */}
            <div
              className="absolute top-0 left-0 h-full bg-[#003366]"
              style={{
                width: `${progressAnimation}%`,
                transition: 'width 1s cubic-bezier(0.1, 0.4, 0.2, 1)' // Slow to fast easing
              }}
            ></div>

            {/* Mercury-like bubble effect with improved animation */}
            <div
              className="absolute top-0 h-full w-4 bg-[#003366] rounded-full"
              style={{
                left: `calc(${progressAnimation}% - 8px)`,
                display: progressAnimation > 0 ? 'block' : 'none',
                boxShadow: '0 0 0 2px rgba(0, 51, 102, 0.3)',
                transition: 'left 1s cubic-bezier(0.1, 0.4, 0.2, 1)' // Matching slow to fast easing
              }}
            ></div>
          </>
        )}

        {/* Month markers positioned on the progress bar */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          {monthlySpendData.map((data, index) => (
            <div
              key={index}
              className="absolute top-0 h-full flex items-center justify-center"
              style={{
                left: `${getMonthPosition(index)}%`,
                transform: 'translateX(-50%)',
                zIndex: 30
              }}
              ref={el => monthMarkersRef.current[index] = el}
            >
              <div
                className="relative cursor-pointer pointer-events-auto"
                onMouseEnter={() => handleTooltipPosition(index)}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <div className={`h-3 w-3 rounded-full flex items-center justify-center ${index === currentMonthIndex ? 'bg-yellow-400 border border-yellow-600' :
                  hasMonthlyData(index) ? 'bg-white border border-[#003366]' : 'bg-gray-300'
                  }`}>
                  {hasMonthlyData(index) && <div className="h-1.5 w-1.5 bg-[#003366] rounded-full"></div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltips fixed positioning */}
      {activeTooltip !== null && (
        <div
          className="fixed bg-white border border-gray-200 rounded-md shadow-lg text-xs z-50 p-2 w-48"
          style={tooltipStyles}
        >
          <div className="font-semibold text-[#003366]">
            {monthlySpendData[activeTooltip].month} {currentYear}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-600">Monthly Spend:</span>
            <span className="font-medium">{formatCurrency(monthlySpendData[activeTooltip].spend)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-600">% of Budget:</span>
            <span className="font-medium">{getMonthPercentOfBudget(monthlySpendData[activeTooltip].spend)}%</span>
          </div>
          {activeTooltip === currentMonthIndex && (
            <div className="flex justify-between mt-1 text-[#003366]">
              <span>Forecast:</span>
              <span className="font-medium">{formatCurrency(currentMonthForecast)}</span>
            </div>
          )}
          {/* Arrow pointing up to the circle */}
          <div
            className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full"
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid white'
            }}
          ></div>
        </div>
      )}

      {/* Month labels */}
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        {monthlySpendData.map((data, index) => (
          <div
            key={index}
            className={`${index === currentMonthIndex ? 'text-[#003366] font-bold' :
              hasMonthlyData(index) ? 'text-[#003366] font-medium' : ''
              }`}
          >
            {data.month}
          </div>
        ))}
      </div>

      {/* Analyze button and quick analysis panel */}
      <div className="relative flex justify-end mt-4">
        <button
          ref={analyzeButtonRef}
          onClick={handleAnalyzeClick}
          className="flex items-center bg-[#003366] text-white text-sm py-2 px-4 rounded-md hover:bg-[#002244] transition-colors"
        >
          <TrendingUp size={16} className="mr-2" />
          Analyze
        </button>

        {/* Quick Analysis Panel */}
        {showAnalysis && (
          <div
            ref={analysisRef}
            className="absolute top-full right-0 mt-2 w-80 p-4 border border-gray-200 rounded-md bg-white shadow-lg z-20"
          >
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-[#003366]">Budget Analysis</h4>
              <button
                onClick={() => setShowAnalysis(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>

            {/* Status Summary */}
            <div className={`${budgetStatus.bgColor} p-3 rounded-md mb-3`}>
              <div className="flex items-center">
                <AlertCircle size={18} className={budgetStatus.color} />
                <span className={`font-bold ml-2 ${budgetStatus.color}`}>{budgetStatus.status}</span>
              </div>
              <p className="text-sm mt-1">{budgetStatus.message}</p>
            </div>

            {/* Key Metrics */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Average Monthly Spend:</span>
                <span className="font-semibold">{formatCurrency(averageMonthlySpend)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Highest Month:</span>
                <span className="font-semibold">{highestSpendingMonth.month} ({formatCurrency(highestSpendingMonth.spend)})</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Projected Annual:</span>
                <span className="font-semibold">{formatCurrency(projectedAnnualSpend)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Budget Remaining:</span>
                <span className="font-semibold">{formatCurrency(Math.max(0, annualBudget - amountSpent))}</span>
              </div>
            </div>

            {/* Get Full Report */}
            <button
              onClick={handleGetDetailedReport}
              className="flex items-center justify-center w-full bg-gray-100 text-sm py-2 px-3 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Download size={16} className="mr-2" />
              Get Detailed Report
            </button>
          </div>
        )}
      </div>

      {/* Loading and notification animation keyframes */}
      <style jsx>{`
  @keyframes progressLoading {
    0% {
      left: -33.33%;
    }
    100% {
      left: 100%;
    }
  }
  
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translate(-50%, -20px); }
    10% { opacity: 1; transform: translate(-50%, 0); }
    90% { opacity: 1; transform: translate(-50%, 0); }
    100% { opacity: 0; transform: translate(-50%, -20px); }
  }
`}</style>

    </div>
  );
};

export default AnnualBudgetTracker;